<template>
  <div
    class="ticket-row ticket-name"
    :class="{ 'ticket-ordered': !!amount && !readOnly, disabled: inactive }"
  >
    <div class="">
      <slot name="icon">
        <Ticket />
      </slot>
    </div>
    <!--
      `grow` only when the button is there. The row is a grid item that already fills its
      1fr column, so growing this block unconditionally would claim space it did not claim
      before on EVERY row — unseated, add-on and sold-out included — and push the
      description's float-right chevron outward. No test would catch that.
    -->
    <div :class="{ grow: showBestAvailable }">
      <!--
        Below `sm` the control drops to its own line so the name gets the whole cell — the grid
        is `1fr auto` there, and `shrink-0` on the control meant the NAME was what gave way.
        `items-start` is load-bearing for the CONTROL: a flex item is blockified, so a column
        would otherwise stretch it across the full width. The name does not care either way.

        Unconditional, unlike `grow` above, and safely so: a row with no control leaves one flex
        item here, and a lone item's row-direction main size and its column-direction cross size
        under `items-start` both resolve to the same fit-content, so the name wraps where it
        already did. `justify-content` has no free space to hand out in an auto-height column
        and `gap-2` never applies to one child. `grow` is different because it changes THIS
        block's width, which is what would move the chevron.
      -->
      <div class="flex flex-col items-start gap-2 sm:flex-row sm:items-center sm:justify-between">
        <span class="text-lg">{{ ticket.name }}</span>
        <template v-if="showBestAvailable">
          <!--
            The design draws this as the very same outline/sm button in its disabled state, with
            a check where the label starts — so it is that button, not a bespoke chip. What it
            says is true of the control as well as the tier: while these seats are held there is
            no best available left to find here, and the buyer undoes them on the chart.

            `soft-disabled` rather than the native `disabled`, because a disabled button leaves
            the tab order and cannot take focus — and the modal is about to hand focus back to a
            button this swap has already removed (see the watcher below).
          -->
          <VPButton
            v-if="bestAvailableSelected"
            ref="selectedChip"
            variant="outline"
            size="sm"
            type="button"
            icon-prefix="mdi:check"
            soft-disabled
            tabindex="-1"
            data-testid="best-available-selected"
            class="shrink-0"
            :aria-label="$gettext('Best available seats selected for %{ticket}', { ticket: ticket.name })"
          >
            <span class="text-xs leading-snug">{{ $gettext('Best available selected') }}</span>
          </VPButton>
          <!--
            Only the LABEL steps down: padding, gap, border and variant stay the outline/sm
            button the design pins this control to (see the chip's comment above), which is why
            this is not `size="xs"` on a shared component that has no such size and no design
            for one. The button does end up ~2.75px shorter — a `text-sm` line box at
            `leading-snug` is 19.25px and a `text-xs` one is 16.5px — which is inherent to
            stepping the label down rather than something to correct.

            Sized on an element of its own rather than by `text-xs` among the button's own
            classes. Those would sit beside `SIZE_CLASSES.sm`'s `text-sm` at equal specificity
            in one layer, decided purely by which Tailwind emits last — alphabetically, so
            `text-xs` does win today. That is the collision Tailwind's own docs tell you not to
            author, and nothing in jsdom can see it flip. A child declares its own font-size and
            merely inherits the button's, so there is no collision to resolve.

            `leading-snug` is repeated because `--tw-leading` is registered `inherits: false`,
            so the child would otherwise fall back to `text-xs`'s own line-height. Half a pixel
            on its own; carried for the same reason the button declares it, so the label's
            leading is the button's rather than whatever its size ships with.

            The chip's `mdi:check` is outside the slot and stays at the button's size — `VPIcon`
            renders it at `1em`.
          -->
          <VPButton
            v-else
            variant="outline"
            size="sm"
            type="button"
            class="shrink-0"
            :disabled="!bestAvailable!.chartReady.value"
            :aria-label="$gettext('Find best available seats for %{ticket}', { ticket: ticket.name })"
            @click="bestAvailable!.request(ticket)"
          >
            <span class="text-xs leading-snug">{{ $gettext('Find best available') }}</span>
          </VPButton>
        </template>
      </div>
      <div
        v-if="ticketDescription"
        class=""
      >
        <button
          v-if="canTruncateDescription"
          type="button"
          class="px-4 float-right"
          @click="toggleDescription"
        >
          <iconify-icon
            icon="mdi:chevron-up"
            class="text-2xl transition-transform"
            :class="{ '-rotate-180': descriptionTruncated }"
          />
        </button>
        <span
          class="text-vp-base-500"
          v-html="sanitizeHtml(description)"
        />
      </div>
    </div>
  </div>
  <div
    v-if="amount == 0 && ticket.soldOut"
    :class="{ disabled: inactive }"
    class="ticket-row quantity-buttons sold-out-label row-span-2 sm:row-span-1 sm:col-span-2 p-2 lg:p-4 flex text-2xl font-bold text-vp-base-900 justify-self-stretch justify-end border-r-4 border-r-transparent border-b border-b-vp-base-300"
  >
    {{ $gettext("Sold out") }}
  </div>
  <template v-else>
    <form
      v-if="!readOnly && !hideQuantity"
      class="ticket-row quantity-buttons p-2 lg:p-4 flex"
      :class="{ 'ticket-ordered': !!amount && !readOnly }"
    >
      <ButtonsNumericInput
        :value="desiredAmount"
        :disabled="disabled || inactive"
        :multiplier="ticket.multiple"
        :minimum="ticket.minimum"
        @set="(num: number) => setTicketAmount(num)"
        @add="(delta: number) => addTicketAmount(delta)"
      />
      <Accessible :description="$gettext('Add tickets you want to purchase')" />
    </form>

    <div
      v-if="readOnly && !hideQuantity"
      class="ticket-row quantity-buttons p-2 lg:p-4 flex justify-self-stretch"
    >
      {{ amount }}
    </div>

    <div
      class="ticket-row price"
      :class="{
        'ticket-ordered': !!amount && !readOnly,
        'text-base': readOnly,
        disabled: inactive,
        // With no quantity cell beside it the price would auto-place into the column that
        // cell vacated — the middle one from `sm` up, and only the first of two rows on
        // mobile. Take the vacated column instead, exactly as the sold-out label above does.
        'row-span-2 sm:row-span-1 sm:col-span-2': hideQuantity,
      }"
    >
      <div class="text-right">
        {{
          formatCurrency(ticket.breakdown?.displayPrice ?? 0, {
            currency: props.currency,
          })
        }}
      </div>
      <div
        v-if="includesFees && (ticket.breakdown?.displayPrice ?? 0) > 0"
        class="text-right font-normal uppercase text-vp-base-500 text-xs sm:text-sm"
      >
        {{ $gettext("Incl. Fees") }}*
      </div>
    </div>
  </template>
</template>

<script setup lang="ts">
import sanitizeHtml from 'sanitize-html';
import { formatCurrency } from '@vp/common/util';
import ButtonsNumericInput from './components/ButtonsNumericInput.vue';
import Ticket from './components/Ticket.vue';
import { Checkout, Util } from '@vp/common';
import { useCheckoutStore } from './stores/checkout';
import { computed, nextTick, onBeforeMount, ref, watch, type ComponentPublicInstance } from 'vue';
import { Accessible, VPButton } from '@vp/vue-components';
import { useGettext } from 'vue3-gettext';
import { selectedKey, useBestAvailableSeats } from './composables/useBestAvailableSeats';
import { isVPError } from './types/VPError';
import { useToast } from 'vue-toastification';
import { useDebounceFn } from '@vueuse/core';
import { useTruncatedHtml } from './composables/useTruncatedHtml';
import { useTracking } from './components/tracking/useTracking';
import { trackingCurrency } from './components/tracking/trackingCurrency';
import { cartEventPayload } from './components/tracking/cartEventPayload';

const { LoadingState } = Util;

const checkoutStore = useCheckoutStore();
const toast = useToast();
const { trackEvent } = useTracking();
// Without this binding every template `$gettext` in this file is a TS2339 against the
// typecheck baseline — three of them were already there before the two below.
const { $gettext } = useGettext();
// Undefined on an unseated page and in an isolated mount; the row then offers no
// best-available affordance and is otherwise unchanged.
const bestAvailable = useBestAvailableSeats();

interface Props {
  ticket: Checkout.TicketBlueprint.CheckoutTicketBlueprint;
  includesFees?: boolean;
  readOnly?: boolean;
  currency: string;

  // Temporary solution, until styling is fixed with css. Currently, readOnly causes numbers to be smaller
  inactive?: boolean;

  // A seated event's rows are informational: its seats are picked on the chart, so the row
  // shows what a tier is called and what it costs and offers no way to change an amount.
  // Distinct from `readOnly`, which still reports the count it will not let you edit.
  hideQuantity?: boolean;
}
const props = defineProps<Props>();

// Whether the event can offer best available at all — a chart with no focal point hands back
// arbitrary seats, or fails outright on an event that is plainly available. The backend makes
// that call and the row honours it; a server that predates the field omits it, which normalizes
// to `false` in the mapper, so the affordance stays hidden rather than appearing and failing.
const offersBestAvailable = computed(
  () => checkoutStore.getSelectedEvent?.seatingChartConfig?.bestAvailable === true,
);

// Seated rows only — `hideQuantity` is the page's "seats are picked on the chart" signal,
// so add-on rows (which keep their quantity control) never offer this. A sold-out tier has
// nothing to find, and a read-only row is a summary rather than a way to buy. Gating here
// rather than on the button covers the selected chip too: it is the same affordance in its
// taken state, and an event that cannot offer a run cannot report one either.
const showBestAvailable = computed(() => Boolean(
  props.hideQuantity && bestAvailable && offersBestAvailable.value && !props.readOnly && !props.ticket.soldOut,
));

// This tier's seats as the CART holds them. `seatIds` are seats.io labels — the same alphabet
// the picker's `selectedObjects` and `clearHeldSeats` speak, so the two sides compare directly.
const cartedSeatIds = computed(() =>
  checkoutStore.getCartedSeatTickets
    .filter(({ itemId }) => itemId === props.ticket.id)
    .flatMap(({ seatingInfo }) => seatingInfo?.seatIds || []),
);

// Derived, never remembered. Everything it reads is page- or store-scoped, so a chart rebuild
// is invisible to it, and it reverts on its own the moment those seats leave the cart —
// whether the buyer deselected them, someone else booked them, or the hold token expired.
//
// The intersection is what keeps the row honest in both directions: a tier the buyer picked by
// hand never claims a best-available run, and a hold whose cart write never landed never claims
// seats the buyer does not have.
const bestAvailableSelected = computed(() => {
  const selected = bestAvailable?.selectedSeats.value;

  return Boolean(selected) && cartedSeatIds.value.some(
    seatId => selected!.has(selectedKey(props.ticket.id, seatId)),
  );
});

// The VPButton instance, not an element — `$el` is the <button> it renders.
const selectedChip = ref<ComponentPublicInstance | undefined>(undefined);

// True while THIS row's dialog is open — the channel names the tier it was opened for.
const dialogOpen = computed(() => bestAvailable?.requested.value?.id === props.ticket.id);

// The modal restores focus to whatever had it when it opened: this row's button. On the success
// path the cart writes land before the modal is dismissed, so the row has already swapped that
// button out and the restore hits a detached node — leaving a keyboard buyer on <body>. The row
// is the only place that knows what replaced it, so it hands focus over itself.
watch(dialogOpen, async (open, wasOpen) => {
  if (open || !wasOpen || !bestAvailableSelected.value) {
    return;
  }

  // One tick, so this lands after the modal's own restore — which targets the detached button
  // and is therefore a no-op that leaves focus wherever it already is, rather than a fight.
  await nextTick();
  (selectedChip.value?.$el as HTMLElement | undefined)?.focus();
});

const amount = computed(
  () =>
    checkoutStore.getCartedTickets.find(
      ({ itemId }) => itemId === props.ticket.id,
    )?.count || 0,
);
const desiredAmount = ref(amount.value);

const disabled = computed(
  () => checkoutStore.shoppingCartLoadingState == LoadingState.LOADING,
);

watch(
  () => [amount.value, disabled.value] as const,
  ([newAmount, newLoading]) => {
    if (!newLoading) {
      desiredAmount.value = newAmount;
    }
  },
);

const setTicketAmount = async (num: number) => {
  desiredAmount.value = Math.max(0, num);
  updateTicketAmount();
};

const addTicketAmount = (delta: number) => {
  setTicketAmount(desiredAmount.value + delta);
};

const updateTicketAmount = useDebounceFn(async () => {
  if (desiredAmount.value == amount.value) {
    return;
  }

  const change = desiredAmount.value - amount.value;
  trackEvent(
    change > 0 ? 'add_to_cart' : 'remove_from_cart',
    cartEventPayload(props.ticket, Math.abs(change), trackingCurrency(props.currency)),
  );

  try {
    await checkoutStore.setTicketCount(
      props.ticket.id,
      desiredAmount.value,
    );
  } catch (e) {
    if (isVPError(e)) {
      for (const msg of e.errors) {
        toast.error(msg);
      }
      return;
    }

    throw e;
  }
}, 300);

const ticketDescription = computed(() => props.ticket.description ?? '');

const {
  text: description,
  truncate: descriptionTruncated,
  canTruncate: canTruncateDescription,
} = useTruncatedHtml(ticketDescription, {
  limit: 150,
  startTruncated: false,
  sanitizer: (input: string) => sanitizeHtml(input),
});

const toggleDescription = () => {
  descriptionTruncated.value = !descriptionTruncated.value;
};

onBeforeMount(() => {
  // In order for SSR to render the full description
  descriptionTruncated.value = true;
});
</script>
